/* eslint-disable */
import React, { useEffect, useState, useRef, useContext } from 'react';
import { renderToString } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import UserContext, { UserContextType } from '@/context/UserContext/UserContext';
import TenantContext from '@/context/TenantContext/TenantContext';
import proj4 from 'proj4';

// Openlayers
import { transform } from 'ol/proj';
import OlFormatWKT from 'ol/format/WKT';

// MUI
import { Box, Button, Collapse, Menu, MenuItem, useTheme } from '@mui/material';

// Cesium
import {
  Cartesian2,
  Cartesian3,
  Rectangle,
  Camera,
  Matrix4,
  EasingFunction,
  Color,
  ScreenSpaceEventHandler,
  Viewer,
  Scene,
  Globe,
  Ellipsoid,
  EllipsoidGeodesic,
  HorizontalOrigin,
  VerticalOrigin,
  ProviderViewModel,
  WebMapServiceImageryProvider,
  OpenStreetMapImageryProvider,
  Cesium3DTileset,
  Cartographic,
  PolylineCollection,
  buildModuleUrl,
  PointPrimitiveCollection,
  Credit,
  Resource,
  SceneMode,
  defined,
  Material,
  ScreenSpaceEventType,
  PointPrimitive,
  Polyline,
  Math as CesiumMath,
  Entity,
  NearFarScalar,
  LabelStyle,
  JulianDate,
  CesiumTerrainProvider,
  RequestErrorEvent,
  sampleTerrainMostDetailed,
  ClippingPolygon,
  ClippingPolygonCollection,
  BoundingSphere,
  DistanceDisplayCondition,
  PinBuilder,
  ConstantProperty
} from 'cesium';
import 'cesium/Build/Cesium/Widgets/widgets.css';

import { styled } from '@mui/material';

import useApi, { IApi } from '@/lib/api/useApi';
import authHeader from '@/lib/api/authHeader';
import { DCRecord } from '@/@types/lib/dataController';
import { LokacijeData, GMData } from '@/@types/views/ThreeD';
import OlPoint from 'ol/geom/Point';
import EntityInfoPane from './EntityInfoPane';
import { getOptimalTextColorForBackgroundColor } from '@/lib/colorUtilities';
import ClickedPointTable, { ClickedPoint } from './ClickedPointTable';
import CesiumButton from './CesiumButton';
import SnackbarContext from '@/context/SnackbarContext/SnackbarContext';
import Permissions from '@/lib/permissions';
import wellknown from "wellknown";
import CesiumModelTree, { CesiumModel } from './CesiumModelTree';

// Types
type FlyToOptions = {
  destination: Cartesian3 | Rectangle;
  orientation?: any;
  duration?: number;
  complete?: Camera.FlightCompleteCallback;
  cancel?: Camera.FlightCancelledCallback;
  endTransform?: Matrix4;
  maximumHeight?: number;
  pitchAdjustHeight?: number;
  flyOverLongitude?: number;
  flyOverLongitudeWeight?: number;
  convert?: boolean;
  easingFunction?: EasingFunction.Callback;
};

type Point = {
  cartographic?: Cartographic;
  latitude?: number;
  longitude?: number;
} & PointPrimitive;

type SourceData = {
  folders: string[];
  files: string[];
  level_of_detail: string[];
  count: number;
  names?: string[];
  geom_overlap: string;
};

type ModelPin = {
  entity: Entity;
  indices: number[];
};

const MODEL_PIN_PREFIX = 'model-pin-';
// metres above the target when flying to ?cx&cy
const CAMERA_HEIGHT_OFFSET = 250;

const DH = 12;

const GS_URL = process.env.REACT_APP_GEOSERVERPATH;

const COPERNICUS_WMS = 'https://image.discomap.eea.europa.eu/arcgis/services/GioLand/VHR_2021_LAEA/ImageServer/WMSServer';

const apiPath = process.env.REACT_APP_APIPATH;

const LINEPOINTCOLOR = Color.RED;
const LINEWIDTH = 3;

// @ts-ignore
window.CESIUM_BASE_URL = '/static/Cesium/';

const categories = [
  { id: 1, color: Color.RED },
  { id: 2, color: Color.BLUE },
  { id: 3, color: Color.GREEN },
  { id: 4, color: Color.YELLOW },
  { id: 5, color: Color.ORANGE },
  { id: 6, color: Color.PURPLE },
  { id: 7, color: Color.PINK },
  { id: 8, color: Color.CYAN },
  { id: 9, color: Color.LIME },
];

const CesiumPage = () => {
  const { t } = useTranslation();
  const { i18n } = useTranslation();

  const userContext = useContext(UserContext) as UserContextType;
  const tenantContext = useContext(TenantContext);

  const [drawAllowed, setDrawAllowed] = useState(false);
  const [drawActive, setDrawActive] = useState(false);
  const [collectAllowed, setCollectAllowed] = useState(false);
  const [refresh, setRefreshToken] = useState(false);
  const [handler, setHandler] = useState<ScreenSpaceEventHandler | null>(null);

  const [sourceCount, setSourceCount] = useState<number | null>(null);
  const [levelOfDetail, setLevelOfDetail] = useState<string[]>([]);
  const [clippingPolygons, setClippingPolygons] = useState<ClippingPolygon[]>([]);
  const [locationRecords, setLocationRecords] = useState<LokacijeData[]>([]);
  const [grobnaMjestaRecords, setGrobnaMjestaRecords] = useState<GMData[]>([]);
  const [showEntities, setShowEntities] = useState(true);
  const [selectedEntityRecord, setSelectedEntityRecord] = useState<DCRecord | undefined>(undefined);
  const [selectedEntityId, setSelectedEntityId] = useState(0);
  const [selectedEntityType, setSelectedEntityType] = useState<'lokacija' | 'grobno_mjesto' | null>(null);
  const [trackedEntityId, setTrackedEntityId] = useState(0);
  const [isOpenGFI, setIsOpenGFI] = useState(false);

  const [userWarned, setUserWarned] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState<number>(0);
  const [collectedPoints, setCollectedPoints] = useState<ClickedPoint[]>([]);
  const [collectAnchorEl, setCollectAnchorEl] = React.useState<null | HTMLElement>(null);

  const theme = useTheme();
  const navigate = useNavigate();

  // indexed like cesium_folder, failed loads stay undefined so indices don't shift
  const tilesetList = useRef<(Cesium3DTileset | undefined)[]>();

  const [modelNames, setModelNames] = useState<string[]>([]);
  const [modelVisible, setModelVisible] = useState<boolean[]>([]);
  const [modelLoaded, setModelLoaded] = useState<boolean[]>([]);
  const [showModelTree, setShowModelTree] = useState(false);
  const modelVisibleRef = useRef<boolean[]>([]);
  const modelPins = useRef<ModelPin[]>([]);
  const modelTreeAutoClosed = useRef(false);

  const viewer = useRef<Viewer>(); // new Viewer('cesiumContainer');
  const camera = useRef<Camera>();
  const scene = useRef<Scene>();
  const globe = useRef(new Globe(Ellipsoid.WGS84));
  globe.current.baseColor = Color.BLACK;
  // setting next like to true caused model to be under terrain :/
  globe.current.depthTestAgainstTerrain = false;
  const ellipsoid = useRef(Ellipsoid.WGS84);
  const geodesic = useRef(new EllipsoidGeodesic());

  // Point collection
  const collectedPointEntities = useRef<PointPrimitiveCollection>(new PointPrimitiveCollection());

  // Measure
  const points = useRef<PointPrimitiveCollection>();
  const point1 = useRef<PointPrimitive>();
  const point2 = useRef<PointPrimitive>();
  const point1GeoPosition = useRef<Cartographic>();
  const point2GeoPosition = useRef<Cartographic>();
  const point3GeoPosition = useRef<Cartographic>();

  const polylines = useRef<PolylineCollection>();
  const polyline1 = useRef<Polyline>();
  const polyline2 = useRef<Polyline>();
  const polyline3 = useRef<Polyline>();

  const distanceLabel = useRef<Entity>();
  const verticalLabel = useRef<Entity>();
  const horizontalLabel = useRef<Entity>();

  const pointNum = useRef<{ [key: number]: number }>({
    1: 1,
    2: 1,
    3: 1,
    4: 1,
    5: 1,
    6: 1,
    7: 1,
    8: 1,
    9: 1,
  });

  const wkt = new OlFormatWKT();

  const labelStyle = {
    font: '14px monospace',
    showBackground: true,
    horizontalOrigin: HorizontalOrigin.CENTER,
    verticalOrigin: VerticalOrigin.CENTER,
    pixelOffset: new Cartesian2(0, 0),
    eyeOffset: new Cartesian3(0, 0, -10),
    fillColor: Color.WHITE,
    text: '',
    disableDepthTestDistance: Number.POSITIVE_INFINITY,
  };

  // used when opening 3d model from the same tab as the main map
  /* const {state} = useLocation();
  const cx = state?.cx;
  const cy = state?.cy;
  const cz = state?.cz; */

  // used when opening 3d model from a new tab
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const snackbarContext = useContext(SnackbarContext);

  const cx = searchParams.get('cx') !== null ? parseFloat(searchParams.get('cx') as string) : undefined;
  const cy = searchParams.get('cy') !== null ? parseFloat(searchParams.get('cy') as string) : undefined;
  const cz = searchParams.get('cz') !== null ? parseFloat(searchParams.get('cz') as string) : undefined;
  const modelParam = searchParams.get('model') !== null ? parseInt(searchParams.get('model') as string, 10) : undefined;

  const handleRefresh = () => {
    setRefreshToken((prev) => !prev);
  };

  proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');
  proj4.defs(
    'EPSG:3857',
    '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs'
  );

  const replaceTooltipTextLanguage = () => {
    const cesiumClassnames = [
      'cesium-navigation-help-pan',
      'cesium-navigation-help-details',
      'cesium-navigation-help-zoom',
      'cesium-navigation-help-rotate',
      'cesium-baseLayerPicker-sectionTitle',
      'cesium-navigation-help-button',
      'cesium-navigation-help-tilt',
      'cesium-home-button',
    ];

    let count = 0;
    for (const classnameOriginal of cesiumClassnames) {
      const elements = document.getElementsByClassName(classnameOriginal);
      const classname = classnameOriginal.replaceAll('-', '_');
      for (const element of elements) {
        //@ts-ignore
        if (element.title !== '' && element.title !== null && element.title !== undefined) {
          //@ts-ignore
          element.title = t('3D:' + classname);
          continue;
        }
        if (classnameOriginal === 'cesium-navigation-help-details') {
          // multiple elements with same class require different translations
          count += 1;
          element.textContent = t('3D:' + classname + '_' + String(count));
        } else {
          element.textContent = t('3D:' + classname);
        }
      }
    }
  };

  const cesiumButtons = document.querySelectorAll<HTMLButtonElement>('.cesium-button');
  cesiumButtons.forEach((button) => {
    button.style.backgroundColor = theme.palette.primary.main;
    button.style.fill = getOptimalTextColorForBackgroundColor(theme.palette.primary.main);
    button.style.color = getOptimalTextColorForBackgroundColor(theme.palette.primary.main);
    button.style.border = '0';
    button.style.boxShadow = '0';
    button.style.transition = 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms';

    // Adding hover, focus, and active event listeners
    button.addEventListener('mouseenter', () => {
      button.style.backgroundColor = theme.palette.primary.dark;
      button.style.boxShadow = '0';
    });
    button.addEventListener('mouseleave', () => {
      button.style.backgroundColor = theme.palette.primary.main;
      button.style.boxShadow = '0';
    });
  });

  const getColorByStatus = (status: number) => {
    switch (status) {
      case 1:
        return '#3944BC';
      case 2:
        return '#990000';
      case 3:
        return '#009999';
      case 5:
        return '#808c00';
      default:
        return theme.palette.primary.main;
    }
  };

  const getColorByHasVlasnik = (hasVlasnik: boolean) => {
    if (hasVlasnik === true) return '#2bff00';
    if (hasVlasnik === false) return '#ff4141';
    return theme.palette.primary.main;
  };

  const formatGMLabel = ({
    odjel,
    razred,
    polozaj,
    polje,
    red,
    broj,
  }: {
    odjel?: string | null;
    razred?: string | null;
    polozaj?: string | null;
    polje?: string | null;
    red?: string | null;
    broj?: string | null;
  }) => {
    return [odjel, razred, polozaj, polje, red, broj]
      .filter((v) => v !== null && v !== undefined && v !== '')
      .join('-');
  };

  useEffect(() => {
    const apiInstance = useApi();

    const url_lokacije = 'threeD/locations';
    apiInstance.get(url_lokacije).then((resp) => {
      if (resp.data && Array.isArray(resp.data) && resp.data.length !== 0) setLocationRecords(resp.data);
    });

    if (!userContext.hasAnyPermission([Permissions.Groblja])) return;
    const url_gm = 'threeD/grobna-mjesta';
    apiInstance.get(url_gm).then((resp) => {
      if (resp.data && Array.isArray(resp.data) && resp.data.length !== 0) setGrobnaMjestaRecords(resp.data);
    });
  }, []);

  useEffect(() => {
    if (locationRecords.length > 0 && viewer.current) {
      for (const locationKey in locationRecords) {
        const ENTITY_PADDING = 0;

        const location = locationRecords[locationKey]; // contains wkt,kc,kb,id

        const geometry = wkt.readGeometry(location.wkt);
        let coordsFromWKT;
        if (geometry instanceof OlPoint) {
          coordsFromWKT = geometry.getCoordinates();
        } else {
          continue;
        }
        const [cx, cy] = transform([coordsFromWKT[0], coordsFromWKT[1]], 'EPSG:3765', 'EPSG:4326');

        //console.log(location);

        // TODO: FIND WAY TO UPDATE TEXT ON TRANSLATION CHANGE
        const newEntity = viewer.current.entities.add({
          id: location.id + '',
          //name: t("komunalni_obveznici.kc")+": "+location.kc+", "+t("objekti.kucni_broj")+": "+location.kb,
          name: t('komunalni_obveznici.lok'),
          allowPicking: false,
          position: Cartesian3.fromDegrees(cx, cy, coordsFromWKT[2] + ENTITY_PADDING),
          point: {
            pixelSize: 8,
            color: Color.fromCssColorString(getColorByStatus(location.lokacija_status_id)),
            outlineColor: Color.BLACK,
            outlineWidth: 2,
            scaleByDistance: new NearFarScalar(200, 1, 1000, 0.75),
          },
          description: renderToString(<>{'Loading...'}</>),
          label: {
            text: location.kb,
            font: '20pt "Roboto", monospace',
            style: LabelStyle.FILL_AND_OUTLINE,
            outlineWidth: 4,
            verticalOrigin: VerticalOrigin.BOTTOM,
            pixelOffset: new Cartesian2(0, -15),
            translucencyByDistance: new NearFarScalar(200, 1, 300.0, 0),
            scaleByDistance: new NearFarScalar(105, 1, 200.0, 0.5),
          },
          properties: {
            record: {
              ...location,
              adresa:
                (location.ul_ime ? location.ul_ime : '') +
                ' ' +
                (location.kb ? location.kb : '') +
                ', ' +
                (location.na_ime ? location.na_ime : ''),
              type: 'lokacija',
            },
          },
        } as Entity.ConstructorOptions);
      }
    }
  }, [locationRecords, viewer.current]);

  useEffect(() => {
    if (grobnaMjestaRecords.length > 0 && viewer.current) {
      for (const gmKey in grobnaMjestaRecords) {
        const ENTITY_PADDING = 0;

        const gm = grobnaMjestaRecords[gmKey];
        const geometry = wkt.readGeometry(gm.wkt);
        let coordsFromWKT;
        if (geometry instanceof OlPoint) {
          coordsFromWKT = geometry.getCoordinates();
        } else {
          continue;
        }
        const [cx, cy] = transform([coordsFromWKT[0], coordsFromWKT[1]], 'EPSG:3765', 'EPSG:4326');

        //console.log(gm);

        // TODO: FIND WAY TO UPDATE TEXT ON TRANSLATION CHANGE
        const newEntity = viewer.current.entities.add({
          id: gm.id + '',
          name: t('grobna_mjesta.grobno_mjesto'),
          allowPicking: false,
          position: Cartesian3.fromDegrees(cx, cy, coordsFromWKT[2] + ENTITY_PADDING),
          point: {
            pixelSize: 5,
            color: Color.fromCssColorString(getColorByHasVlasnik(gm.has_vlasnik)),
            outlineColor: Color.BLACK,
            outlineWidth: 2,
            scaleByDistance: new NearFarScalar(200, 1, 1000, 0.75),
          },
          description: renderToString(<>{'Loading...'}</>),
          label: {
            text: formatGMLabel(gm),
            font: 'bold 9pt "Roboto", monospace',
            fillColor: Color.BLACK,
            outlineWidth: 3,
            outlineColor: Color.WHITE,
            style: LabelStyle.FILL_AND_OUTLINE,
            showBackground: true,
            backgroundColor: Color.WHITE.withAlpha(0.75),
            verticalOrigin: VerticalOrigin.BOTTOM,
            pixelOffset: new Cartesian2(0, -15),
            scaleByDistance: new NearFarScalar(100, 1, 500, 0),
          },
          properties: {
            record: {
              ...gm,
              grobno_mjesto: formatGMLabel(gm),
              type: 'grobno_mjesto'
            },
          },
        } as Entity.ConstructorOptions);
      }
    }
  }, [grobnaMjestaRecords, viewer.current]);


  useEffect(() => {
    if (viewer.current) {
      viewer.current.selectedEntityChanged.addEventListener((selectedEntity: Entity) => {
        const pin = modelPins.current.find((p) => p.entity === selectedEntity);
        if (pin && viewer.current) {
          viewer.current.selectedEntity = undefined;
          flyToModels(pin.indices);
          return;
        }
        if (defined(selectedEntity)) {
          if (defined(selectedEntity.id)) {
            if (selectedEntity.properties) {
              setSelectedEntityRecord(selectedEntity.properties.getValue(JulianDate.now()).record);
              setSelectedEntityId(parseInt(selectedEntity.id));
              setSelectedEntityType(selectedEntity.properties.getValue(JulianDate.now()).record.type ?? null);
              setIsOpenGFI(true);
            }
          } else {
            handleCloseInfoPane();
          }
        } else {
          handleCloseInfoPane();
        }
      });

      viewer.current.trackedEntityChanged.addEventListener((trackedEntity) => {
        // double click on a pin would lock the camera to it, keep it free
        if (trackedEntity && viewer.current && modelPins.current.some((p) => p.entity === trackedEntity)) {
          viewer.current.trackedEntity = undefined;
          return;
        }
        if (trackedEntity !== undefined) {
          setTrackedEntityId(parseInt(trackedEntity.id));
        } else {
          setTrackedEntityId(0);
        }
      });
    }
  }, [viewer.current]);

  useEffect(() => {
    const apiInstance = useApi();

    async function loadConfig() {
      try {
        const resp = await apiInstance.get("threeD/tiles/config/count");

        const source = resp.data as SourceData;
        const clippingPolygons: ClippingPolygon[] = [];
        if (source.geom_overlap) {
          const geometry = wellknown.parse(source.geom_overlap);
          if (geometry && geometry.type === "MultiPolygon") {
            for (const polygonCoords of geometry.coordinates) {
              const outerRing = polygonCoords[0]; 
              const positions = outerRing.map(coord => 
                Cartesian3.fromDegrees(coord[0], coord[1])
              );
              clippingPolygons.push(new ClippingPolygon({ positions }));
            }
          }
        }
        setClippingPolygons(clippingPolygons);

        let lodArray: string[] = Array(source.count).fill('0');
        if (
          Array.isArray(source.level_of_detail) &&
          source.level_of_detail.length === source.count &&
          source.level_of_detail.every((v) => v === '0' || v === '1')
        ) {
          lodArray = source.level_of_detail;
        }

        setLevelOfDetail(lodArray);
        setModelNames(Array.from({ length: source.count }, (_, i) => source.names?.[i] || `${i + 1}`));
        setShowModelTree(source.count > 1);
        setSourceCount(source.count);
      } catch {
        setSourceCount(null);
      }
    }


    loadConfig();

  }, []);

  const resourceRetryCallback = (resource?: Resource, error?: RequestErrorEvent) => {
    if (error) {
      if (error.statusCode === 401) navigate('/');
      return true;
    } else return false;
  };

  useEffect(() => {
    if (sourceCount === null) return;

    async function initialize() {
      // Ion.defaultAccessToken = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiJiMDVhZmY2Yi1mYTRjLTQ1NTYtODJmZC0wOTZlZDE4Yzg1MzEiLCJpZCI6MTE1Nzc0LCJpYXQiOjE2NjkxNTI5NzV9.3yUjYxWXb2KtUMQjH9WWd1DQDVcN-8WnxJeTAO8mmXI";
      // ne zelimo koristiti Ion

      // Viewer
      if (!viewer.current) {
        viewer.current = new Viewer('cesiumContainer', {
          animation: false,
          baseLayerPicker: true,
          geocoder: false,
          // Ovo ne radi?
          maximumRenderTimeChange: Infinity,
          requestRenderMode: true,
          scene3DOnly: true,
          timeline: false,
          infoBox: false,
          selectionIndicator: true,
          globe: globe.current,
          terrainProviderViewModels: [],
        });

        // DGU DOF base layer view model
        const dguDofViewModel = new ProviderViewModel({
          name: 'DGU DOF',
          tooltip: 'DGU DOF',
          iconUrl:
            'https://geoportal.dgu.hr/wms?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&FORMAT=image%2Fpng8&TRANSPARENT=true&LAYERS=DOF&TILED=true&WIDTH=256&HEIGHT=256&CRS=EPSG%3A3857&STYLES=&BBOX=2015491.5618235283%2C5257033.057341281%2C2016103.0580498097%2C5257644.553567563',
          creationFunction: () =>
            new WebMapServiceImageryProvider({
              url: `${apiPath}core/wms/dgu?`,
              layers: 'DOF',
              crs: 'EPSG:3857',
              parameters: {
                format: 'image/png',
                transparent: true,
              }
            }),
        });

        // OpenStreetMap base layer view model
        const osmViewModel = new ProviderViewModel({
          name: 'OpenStreetMap',
          iconUrl: buildModuleUrl('Widgets/Images/ImageryProviders/openStreetMap.png'),
          tooltip:
            'OpenStreetMap (OSM) is a collaborative project to create a free editable map of the world.\nhttp://www.openstreetmap.org',
          creationFunction() {
            return new OpenStreetMapImageryProvider({
              url: 'https://a.tile.openstreetmap.org/',
            });
          },
        });

        // Set the imageryProviderViewModels property to an array containing only the two base layers you want to keep
        // EU wide orthophoto, for tenants outside Croatia where DGU has nothing
        const copernicusViewModel = new ProviderViewModel({
          name: 'Copernicus VHR 2021',
          tooltip: 'Copernicus Land Monitoring Service, Very High Resolution imagery 2021 (EEA)',
          iconUrl: `${COPERNICUS_WMS}?SERVICE=WMS&VERSION=1.1.1&REQUEST=GetMap&LAYERS=VHR_2021_LAEA&STYLES=&SRS=EPSG:4326&BBOX=15.44,45.44,15.46,45.46&WIDTH=64&HEIGHT=64&FORMAT=image/png`,
          creationFunction: () =>
            new WebMapServiceImageryProvider({
              url: COPERNICUS_WMS,
              layers: 'VHR_2021_LAEA',
              credit: new Credit('© European Union, Copernicus Land Monitoring Service 2021, European Environment Agency (EEA)'),
            }),
        });

        const isCroatianTenant = (tenantContext?.defaultLanguage ?? 'hr') === 'hr';

        viewer.current.baseLayerPicker.viewModel.imageryProviderViewModels = [dguDofViewModel, copernicusViewModel, osmViewModel];
        viewer.current.baseLayerPicker.viewModel.selectedImagery = isCroatianTenant ? dguDofViewModel : copernicusViewModel;
      }

      if (viewer.current) {
        camera.current = viewer.current.camera;
        scene.current = viewer.current.scene;
        points.current = scene.current.primitives.add(new PointPrimitiveCollection());
        collectedPointEntities.current = scene.current.primitives.add(new PointPrimitiveCollection());
        polylines.current = scene.current.primitives.add(new PolylineCollection());

        setHandler(new ScreenSpaceEventHandler(scene.current.canvas));

        // var ellipsoid = Ellipsoid.WGS84;
        // var geodesic = new EllipsoidGeodesic();

        // define layers
        // 	layers.push(new ImageryLayer(new WebMapServiceImageryProvider({
        //   url: gs_url,
        //   layers: "ENA:105_DOF",
        //   crs: "EPSG:3857",
        //   parameters: {
        //     format: "image/vnd.jpeg-png",
        //     transparent: true,
        //   }
        // })))
        // layers.push(new ImageryLayer(new WebMapServiceImageryProvider({
        //     url: gs_url,
        //     layers: "ENA:105_DKP",
        //     crs: "EPSG:3857",
        //     parameters: {
        //       format: "image/vnd.jpeg-png",
        //       transparent: true,
        //     }
        // })))
        // layers.push(new ImageryLayer(new WebMapServiceImageryProvider({
        //     url: gs_url,
        //     layers: "ENA:105_KB",
        //     crs: "EPSG:3857",
        //     parameters: {
        //       format: "image/vnd.jpeg-png",
        //       transparent: true,
        //     }
        // })))

        // add layers to viewer
        // layers.forEach((layer) => viewer.current.imageryLayers.add(layer))

        // Prehnit d.o.o. logo
        viewer.current.creditDisplay.addStaticCredit(new Credit('<a href="https://prehnit.hr"><img src="logo.png"/></a>'));

        // tileset

        //@ts-ignore Checked at the start of useEffect, safe to ignore null warning
        const tilesets: (Cesium3DTileset | undefined)[] = Array(sourceCount).fill(undefined);
        const authorizationHeader = authHeader();

        const cesiumPath = process.env.REACT_CESIUM_APIPATH?.trim();

        //@ts-ignore Checked at the start of useEffect, safe to ignore null warning
        for (let i = 0; i < sourceCount; i++) {
          // const tilesUrl = `${cesiumPath}tiles/${i}/config`; // use when using GO service
          const tilesUrl = `${cesiumPath}tiles/${i}/config`;
          const lod = levelOfDetail[i];
          // console.log(lod)
          // console.log("tilesurl",tilesUrl)

          const tilesResource = new Resource({
            url: tilesUrl,
            headers: authorizationHeader,
            // uncomment when using GO service
            // retryAttempts: 1,
            // retryCallback: resourceRetryCallback
          });

          try {
            const newSet = await Cesium3DTileset.fromUrl(tilesResource, {
              dynamicScreenSpaceError: true,
              ...(lod === '0'
                ? {
                    immediatelyLoadDesiredLevelOfDetail: false,
                    maximumScreenSpaceError: 8,
                    skipLevelOfDetail: false,
                  }
                : {
                    immediatelyLoadDesiredLevelOfDetail: false,
                    maximumScreenSpaceError: 2,
                    skipLevelOfDetail: true,
                    dynamicScreenSpaceError: true,
                  }),
            });

            tilesets[i] = newSet;
          } catch (error) {
            console.log(error);
          }
        }

        tilesetList.current = tilesets;

        // Konfiguriraj što radi home gumb
        viewer.current.homeButton.viewModel.command.beforeExecute.addEventListener((commandInfo) => {
          const loaded = (tilesetList.current ?? []).map((tileset, i) => (tileset ? i : -1)).filter((i) => i >= 0);
          const visible = loaded.filter((i) => modelVisibleRef.current[i] !== false);
          flyToModels(visible.length > 0 ? visible : loaded, false);
          commandInfo.cancel = true;
        });

        // Add tileset to viewer and set initial camera position
        scene.current.screenSpaceCameraController.enableCollisionDetection = true;

        tilesets.forEach((tileset, index) => {
          if (viewer.current && tileset) {
            viewer.current.scene.primitives.add(tileset);
            if (index === 0 && clippingPolygons.length > 0) {
              tileset.clippingPolygons =
                new ClippingPolygonCollection({
                  polygons: clippingPolygons
                });
            }
          }
        });

        addModelPins(tilesets);
        const initialVisible = tilesets.map(() => true);
        modelVisibleRef.current = initialVisible;
        setModelVisible(initialVisible);
        setModelLoaded(tilesets.map((tileset) => !!tileset));

        // jump there instantly while terrain loads, initialFlight refines it
        if (cx && cy) flyToCoordinates((cz || CAMERA_HEIGHT_OFFSET), 0);
        else flyToModels(getStartModelIndices(), false, 0);

        const terrainUrl = `${cesiumPath}terrain`;
        // console.log("terrainurl",terrainUrl)

        const terrainResource = new Resource({
          url: terrainUrl,
          headers: authorizationHeader,
          // uncomment when using GO service
          // retryAttempts: 1,
          // retryCallback: resourceRetryCallback
        });

        const terrainProvider = await CesiumTerrainProvider.fromUrl(terrainResource, { requestVertexNormals: true });
        viewer.current.terrainProvider = terrainProvider;

        replaceTooltipTextLanguage();

        initialFlight(terrainProvider);
      }
    }
    initialize();
  }, [sourceCount, clippingPolygons]);

  useEffect(() => {
    replaceTooltipTextLanguage();
  }, [i18n.language]);

  const initialFlight = (terrainProvider: CesiumTerrainProvider) => {
    if (cx && cy) {
      if (cz) {
        flyToCoordinates(cz);
      } else {
        // missing terrain tiles give undefined height, which used to fly the camera to NaN
        sampleTerrainMostDetailed(terrainProvider, [Cartographic.fromRadians(cx, cy)])
          .then(([position]) => flyToCoordinates((Number.isFinite(position.height) ? position.height : 0) + CAMERA_HEIGHT_OFFSET))
          .catch((error) => {
            console.error('Error sampling terrain:', error);
            flyToCoordinates(CAMERA_HEIGHT_OFFSET);
          });
      }
    } else {
      // No location given, fly to ?model= or all models, same as home
      flyToModels(getStartModelIndices(), false);
    }
  };

  // cx/cy come from the URL in radians
  const flyToCoordinates = (height: number, duration?: number) => {
    if (!camera.current || !cx || !cy) return;
    const flyOptions: FlyToOptions = {
      destination: Cartographic.toCartesian(new Cartographic(cx, cy, height)),
      duration,
    };
    camera.current.flyTo(flyOptions);
  };

  const getStartModelIndices = () => {
    const list = tilesetList.current ?? [];
    if (modelParam !== undefined && list[modelParam]) return [modelParam];
    return list.map((tileset, i) => (tileset ? i : -1)).filter((i) => i >= 0);
  };

  const flyToModels = (indices: number[], updateUrl = true, duration?: number) => {
    if (!viewer.current || !tilesetList.current) return;
    const spheres = indices
      .map((i) => tilesetList.current?.[i]?.boundingSphere)
      .filter((sphere): sphere is BoundingSphere => !!sphere);
    if (spheres.length === 0) return;

    viewer.current.camera.flyToBoundingSphere(BoundingSphere.fromBoundingSpheres(spheres), { duration });
    if (updateUrl) navigate({ search: `?model=${indices[0]}` }, { replace: true });
  };

  // one pin per group of touching models, so neighbouring parts of a city share a pin
  // names can be plain text or a translation token, plain text passes through untouched
  const getModelLabel = (index: number) => t(modelNames[index], { defaultValue: modelNames[index] });

  const addModelPins = (tilesets: (Cesium3DTileset | undefined)[]) => {
    if (!viewer.current) return;

    const clusters: { indices: number[]; sphere: BoundingSphere }[] = [];
    tilesets.forEach((tileset, index) => {
      if (!tileset) return;
      const sphere = tileset.boundingSphere;
      const cluster = clusters.find((c) => Cartesian3.distance(c.sphere.center, sphere.center) < c.sphere.radius + sphere.radius);
      if (cluster) {
        cluster.indices.push(index);
        cluster.sphere = BoundingSphere.union(cluster.sphere, sphere);
      } else {
        clusters.push({ indices: [index], sphere: BoundingSphere.clone(sphere) });
      }
    });

    const pinImage = new PinBuilder().fromColor(Color.fromCssColorString(theme.palette.primary.main), 32);

    modelPins.current = clusters.map((cluster, k) => {
      const showFrom = new DistanceDisplayCondition(cluster.sphere.radius * 4, Number.POSITIVE_INFINITY);
      const entity = (viewer.current as Viewer).entities.add({
        id: `${MODEL_PIN_PREFIX}${k}`,
        position: cluster.sphere.center,
        billboard: {
          image: pinImage,
          verticalOrigin: VerticalOrigin.BOTTOM,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          distanceDisplayCondition: showFrom,
        },
        label: {
          text: cluster.indices.map(getModelLabel).join(' / '),
          font: '14pt "Roboto", sans-serif',
          style: LabelStyle.FILL_AND_OUTLINE,
          fillColor: Color.WHITE,
          outlineColor: Color.BLACK,
          outlineWidth: 3,
          verticalOrigin: VerticalOrigin.BOTTOM,
          pixelOffset: new Cartesian2(0, -36),
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          distanceDisplayCondition: showFrom,
        },
      });
      return { entity, indices: cluster.indices };
    });
  };

  useEffect(() => {
    modelVisibleRef.current = modelVisible;
    const list = tilesetList.current;
    if (!viewer.current || !list) return;

    list.forEach((tileset, i) => {
      if (tileset) tileset.show = modelVisible[i] !== false;
    });

    // overlap is cut out of model 0 because another model fills it, no neighbour means no hole
    // enabled exists at runtime, Cesium's typings just forgot it
    const clipping = list[0]?.clippingPolygons as (ClippingPolygonCollection & { enabled: boolean }) | undefined;
    if (clipping) clipping.enabled =list.some((tileset, i) => i > 0 && !!tileset && modelVisible[i] !== false);

    modelPins.current.forEach(({ entity, indices }) => {
      const visibleIndices = indices.filter((i) => modelVisible[i] !== false);
      entity.show = visibleIndices.length > 0;
      if (entity.label) entity.label.text = new ConstantProperty(visibleIndices.map(getModelLabel).join(' / '));
    });

    viewer.current.scene.requestRender();
  }, [modelVisible, modelNames, i18n.language]);

  // info pane takes the switcher's spot, switcher comes back when the pane closes
  useEffect(() => {
    if (isOpenGFI && showModelTree) {
      modelTreeAutoClosed.current = true;
      setShowModelTree(false);
    } else if (!isOpenGFI && modelTreeAutoClosed.current) {
      modelTreeAutoClosed.current = false;
      setShowModelTree(true);
    }
  }, [isOpenGFI]);

  const handleToggleModel = (index: number) => {
    setModelVisible((prev) => prev.map((visible, i) => (i === index ? !visible : visible)));
  };

  const handleZoomToModel = (index: number) => {
    setModelVisible((prev) => prev.map((visible, i) => (i === index ? true : visible)));
    flyToModels([index]);
  };

  const models: CesiumModel[] = modelNames.map((_, index) => ({
    index,
    name: getModelLabel(index),
    visible: modelVisible[index] !== false,
    loaded: modelLoaded[index] === true,
  }));

  useEffect(() => {
    // Mouse over the globe to see the cartographic position

    handler?.setInputAction((click: ScreenSpaceEventHandler.PositionedEvent) => {
      // If a billboard or an entity is clicked, prevent further actions
      if (!scene.current || !viewer.current) return;

      const pickedObject = scene.current.pick(click.position);

      if (pickedObject && defined(pickedObject.id)) {
        return;
      }

      const cartesian = scene.current.pickPosition(click.position);

      if (defined(cartesian) && camera.current && collectAllowed && !userWarned) {
        const distance = Cartesian3.distance(camera.current.positionWC, cartesian);
        if (distance >= 30) {
          setUserWarned(true);
          snackbarContext.showNotification(t('3D:warnings.collect'), 'warning', 'dontAutohide');
        }
      }

      // If no object found on click, we can draw shit
      if (drawAllowed) {
        setDrawActive(true);
        if (scene.current && viewer.current && points.current && polylines.current && scene.current.mode !== SceneMode.MORPHING) {
          if (scene.current.pickPositionSupported && defined(pickedObject)) {
            //console.log(cartesian);
            if (defined(cartesian)) {
              if (points.current.length === 2) {
                points.current.removeAll();
                polylines.current.removeAll();
                if (distanceLabel.current) viewer.current.entities.remove(distanceLabel.current);
                if (horizontalLabel.current) viewer.current.entities.remove(horizontalLabel.current);
                if (verticalLabel.current) viewer.current.entities.remove(verticalLabel.current);
              }
              // add first point
              if (points.current.length === 0) {
                point1.current = points.current.add({
                  position: new Cartesian3(cartesian.x, cartesian.y, cartesian.z),
                  color: LINEPOINTCOLOR,
                });
              } // add second point and lines
              else if (points.current.length === 1) {
                point2.current = points.current.add({
                  position: new Cartesian3(cartesian.x, cartesian.y, cartesian.z),
                  color: LINEPOINTCOLOR,
                });
                if (point1.current) {
                  point1GeoPosition.current = Cartographic.fromCartesian(point1.current.position);
                  point2GeoPosition.current = Cartographic.fromCartesian(point2.current.position);
                  point3GeoPosition.current = Cartographic.fromCartesian(
                    new Cartesian3(point2.current.position.x, point2.current.position.y, point1.current.position.z)
                  );

                  const pl1Positions: Cartesian3[] = [
                    Cartesian3.fromRadians(
                      point1GeoPosition.current.longitude,
                      point1GeoPosition.current.latitude,
                      point1GeoPosition.current.height
                    ),
                    Cartesian3.fromRadians(
                      point2GeoPosition.current.longitude,
                      point2GeoPosition.current.latitude,
                      point2GeoPosition.current.height
                    ),
                  ];
                  const pl2Positions = [
                    Cartesian3.fromRadians(
                      point2GeoPosition.current.longitude,
                      point2GeoPosition.current.latitude,
                      point2GeoPosition.current.height
                    ),
                    Cartesian3.fromRadians(
                      point2GeoPosition.current.longitude,
                      point2GeoPosition.current.latitude,
                      point1GeoPosition.current.height
                    ),
                  ];
                  const pl3Positions = [
                    Cartesian3.fromRadians(
                      point1GeoPosition.current.longitude,
                      point1GeoPosition.current.latitude,
                      point1GeoPosition.current.height
                    ),
                    Cartesian3.fromRadians(
                      point2GeoPosition.current.longitude,
                      point2GeoPosition.current.latitude,
                      point1GeoPosition.current.height
                    ),
                  ];

                  polyline1.current = polylines.current.add({
                    show: true,
                    positions: pl1Positions,
                    width: 1,
                    material: new Material({
                      fabric: {
                        type: 'Color',
                        uniforms: {
                          color: LINEPOINTCOLOR,
                        },
                      },
                    }),
                  });
                  polyline2.current = polylines.current.add({
                    show: true,
                    positions: pl2Positions,
                    width: 1,
                    material: new Material({
                      fabric: {
                        type: 'PolylineDash',
                        uniforms: {
                          color: LINEPOINTCOLOR,
                        },
                      },
                    }),
                  });
                  polyline3.current = polylines.current.add({
                    show: true,
                    positions: pl3Positions,
                    width: 1,
                    material: new Material({
                      fabric: {
                        type: 'PolylineDash',
                        uniforms: {
                          color: LINEPOINTCOLOR,
                        },
                      },
                    }),
                  });
                  let labelZ;
                  if (point2GeoPosition.current.height >= point1GeoPosition.current.height) {
                    labelZ = point1GeoPosition.current.height + (point2GeoPosition.current.height - point1GeoPosition.current.height) / 2.0;
                  } else {
                    labelZ = point2GeoPosition.current.height + (point1GeoPosition.current.height - point2GeoPosition.current.height) / 2.0;
                  }

                  addDistanceLabel(point1.current, point2.current, labelZ);
                }
              }
            }
          }
        }
      } else if (collectAllowed && viewer.current && camera.current && scene.current && points.current && event) {
        if (cartesian) {
          const cartographic = Cartographic.fromCartesian(cartesian);
          const latitude = CesiumMath.toDegrees(cartographic.latitude);
          const longitude = CesiumMath.toDegrees(cartographic.longitude);

          const height = cartographic.height;
          const [x, y] = proj4('EPSG:4326', 'EPSG:3765', [longitude, latitude]);

          const newPoint = {
            position: { x, y, z: height },
            category: categories[selectedCategory].id,
            color: categories[selectedCategory].color,
            id: categories[selectedCategory].id + pointNum.current[categories[selectedCategory].id].toString().padStart(2, '0'),
          };

          pointNum.current[newPoint.category] += 1;

          // Add point to state
          setCollectedPoints((prevPoints) => [...prevPoints, newPoint]);

          // Visualize the point
          collectedPointEntities.current.add({
            id: newPoint.id,
            show: true,
            position: cartesian,
            color: newPoint.color,
            description: `Category: ${newPoint.category}`,
          });
        }
        // console.log('collectedPoints:', collectedPoints);
      }
    }, ScreenSpaceEventType.LEFT_CLICK);
  }, [drawAllowed, collectAllowed, selectedCategory, userWarned]);

  const removePoint = (removeId: string, index: number) => {
    collectedPointEntities.current?.remove(collectedPointEntities.current.get(index));
    setCollectedPoints((prevPoints) => [...prevPoints].filter((point) => point.id !== removeId));
    viewer.current?.scene.requestRender();
  };

  useEffect(() => {
    if (viewer.current !== undefined) {
      viewer.current.entities.values.map((entity) => {
        if (entity.id.startsWith(MODEL_PIN_PREFIX)) return;
        entity.show = showEntities;
      });

      const updateVisibility = () => {
        forceCesiumUpdate();
      };

      viewer.current.selectedEntity = undefined;
      viewer.current.trackedEntity = undefined;

      window.requestAnimationFrame(updateVisibility);
    }
  }, [showEntities]);

  const forceCesiumUpdate = () => {
    if (viewer.current) viewer.current.scene.requestRender();
  };

  const addDistanceLabel = (p1: Point, p2: Point, height: number) => {
    p1.cartographic = ellipsoid.current.cartesianToCartographic(p1.position);
    p2.cartographic = ellipsoid.current.cartesianToCartographic(p2.position);
    p1.longitude = CesiumMath.toDegrees(p1.position.x);
    p1.latitude = CesiumMath.toDegrees(p1.position.y);
    p2.longitude = CesiumMath.toDegrees(p2.position.x);
    p2.latitude = CesiumMath.toDegrees(p2.position.y);

    const l1 = { ...labelStyle };
    l1.text = getHorizontalDistanceString(p1, p2);
    if (viewer.current && point1GeoPosition.current) {
      horizontalLabel.current = viewer.current.entities.add({
        position: getMidpoint(p1, p2, point1GeoPosition.current.height),
        label: l1,
      });

      const l2 = { ...labelStyle };
      l2.text = getDistanceString(p1, p2);
      distanceLabel.current = viewer.current.entities.add({
        position: getMidpoint(p1, p2, height),
        label: l2,
      });

      const l3 = { ...labelStyle };
      l3.text = getVerticalDistanceString();
      verticalLabel.current = viewer.current.entities.add({
        position: getMidpoint(p2, p2, height),
        label: l3,
      });
    }
  };

  const getHorizontalDistanceString = (p1: Point, p2: Point) => {
    if (p1.cartographic && p2.cartographic) geodesic.current.setEndPoints(p1.cartographic, p2.cartographic);
    const meters = Number(geodesic.current.surfaceDistance.toFixed(2));
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)} км ⟷`;
    }
    return `${meters} м ⟷`;
  };

  const getVerticalDistanceString = () => {
    if (point1GeoPosition.current && point2GeoPosition.current) {
      const heights = [point1GeoPosition.current.height, point2GeoPosition.current.height];
      const meters = Math.max(...heights) - Math.min(...heights);
      if (meters >= 1000) {
        return `${(meters / 1000).toFixed(1)} км ↥`;
      }
      return `${meters.toFixed(2)} м ↥`;
    }
    return 'unknown';
  };

  const getDistanceString = (p1: Point, p2: Point) => {
    if (p1.cartographic && p2.cartographic) geodesic.current.setEndPoints(p1.cartographic, p2.cartographic);
    const horizontalMeters = Number(geodesic.current.surfaceDistance.toFixed(2));
    if (point1GeoPosition.current && point2GeoPosition.current) {
      const heights = [point1GeoPosition?.current?.height, point2GeoPosition?.current?.height];
      const verticalMeters = Math.max(...heights) - Math.min(...heights);
      const meters = (horizontalMeters ** 2 + verticalMeters ** 2) ** 0.5;

      if (meters >= 1000) {
        return `${(meters / 1000).toFixed(1)} км`;
      }
      return `${meters.toFixed(2)} м`;
    }
    return 'unknown';
  };

  const getMidpoint = (p1: Point, p2: Point, height: number) => {
    const scratch = new Cartographic();
    if (p1.cartographic && p2.cartographic) geodesic.current.setEndPoints(p1.cartographic, p2.cartographic);
    const midpointCartographic = geodesic.current.interpolateUsingFraction(0.5, scratch);
    return Cartesian3.fromRadians(midpointCartographic.longitude, midpointCartographic.latitude, height);
  };

  const handleResetDraw = () => {
    if (
      points.current &&
      polylines.current &&
      viewer.current &&
      distanceLabel.current &&
      horizontalLabel.current &&
      verticalLabel.current
    ) {
      points.current.removeAll();
      polylines.current.removeAll();
      viewer.current.entities.remove(distanceLabel.current);
      viewer.current.entities.remove(horizontalLabel.current);
      viewer.current.entities.remove(verticalLabel.current);
    }
    setDrawActive(false);
    forceCesiumUpdate();
  };

  const handleResetSelect = () => {
    collectedPointEntities.current?.removeAll();
    setCollectedPoints([]);
    viewer.current?.scene.requestRender();

    pointNum.current = {
      1: 1,
      2: 1,
      3: 1,
      4: 1,
      5: 1,
      6: 1,
      7: 1,
      8: 1,
      9: 1,
    };
  };

  //useEffect(()=>{console.log("selentityid",selectedEntityId)},[selectedEntityId]) // selected is for selection indicator
  //useEffect(()=>{console.log("trackedneityid",trackedEntityId)},[trackedEntityId]) // tracked is for camera rotating around object

  const handleCloseInfoPane = () => {
    setIsOpenGFI(false);
    setTrackedEntityId(0);
    setSelectedEntityId(0);
    if (viewer.current) {
      viewer.current.selectedEntity = undefined;
      viewer.current.trackedEntity = undefined;
    }
  };

  const focusOnObject = (id: number) => {
    if (viewer.current !== undefined) {
      viewer.current.entities.values.forEach((entity) => {
        if (entity.id === id + '') {
          if (viewer.current) {
            if (viewer.current.trackedEntity && viewer.current.trackedEntity.id === entity.id) {
              viewer.current.trackedEntity = undefined;
              setTrackedEntityId(0);
            } else {
              viewer.current.trackedEntity = entity;
              setTrackedEntityId(id);
            }
          }
        }
      });
    }
  };

  return (
    <>
      <div id="cesium-draw-toolbar">
        {modelNames.length > 1 && (
          <CesiumButton
            icon="layers"
            tooltipToken={'3D:buttons.models'}
            active={showModelTree}
            onClick={() => {
              modelTreeAutoClosed.current = false;
              setShowModelTree((prev) => !prev);
            }}
          />
        )}
        <CesiumButton
          icon="control_point"
          marginTop={modelNames.length > 1 ? 3 : 0}
          active={collectAllowed}
          disabled={drawAllowed}
          onClick={() => {
            setCollectAllowed((prev) => !prev);
          }}
          tooltipToken={{ active: 'buttons.toggle_selecting_off', notActive: 'buttons.toggle_selecting_on' }}
        />
        {collectAllowed && (
          <Box display="flex" alignItems="center">
            <Button
              variant="contained"
              onClick={(evt) => {
                setCollectAnchorEl(evt.currentTarget);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                zIndex: 999,
                color: getOptimalTextColorForBackgroundColor(theme.palette.primary.main),
                fontWeight: 700,
              }}
            >
              <Box
                width={16}
                height={16}
                border={'1px solid black'}
                borderRadius="50%"
                bgcolor={categories[selectedCategory].color.toCssColorString()}
              />
              {categories[selectedCategory].id}
            </Button>

            <Menu anchorEl={collectAnchorEl} open={Boolean(collectAnchorEl)} onClose={() => setCollectAnchorEl(null)}>
              {categories.map((category, i) => (
                <MenuItem
                  key={category.id}
                  onClick={() => {
                    setSelectedCategory(i);
                    setCollectAnchorEl(null);
                  }}
                >
                  <Box
                    width={16}
                    height={16}
                    border={'1px solid black'}
                    borderRadius="50%"
                    bgcolor={category.color.toCssColorString()}
                    mr={1}
                  />
                  {category.id}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        )}
        <CesiumButton
          tooltipToken={'3D:buttons.delete_points'}
          disabled={collectedPoints.length === 0}
          onClick={() => {
            handleResetSelect();
            handleRefresh();
          }}
          icon="delete_outline"
        />
        <CesiumButton
          tooltipToken={{ active: 'buttons.toggle_drawing_off', notActive: 'buttons.toggle_drawing_on' }}
          icon="design_services_outlined"
          active={drawAllowed}
          disabled={collectAllowed}
          onClick={() => {
            setDrawAllowed((prev) => !prev);
          }}
          marginTop={3}
        />
        <CesiumButton
          tooltipToken={'3D:buttons.delete_drawings'}
          icon="delete_outline"
          disabled={points.current?.length === 0}
          onClick={() => {
            handleResetDraw();
            handleRefresh();
          }}
        />
        {locationRecords.length > 0 && (
          <CesiumButton
            tooltipToken={'3D:buttons.show_locations'}
            icon={'location_on'}
            active={showEntities}
            onClick={() => {
              setShowEntities((showEntities) => !showEntities);
            }}
            marginTop={3}
          />
        )}
      </div>
      <div style={{ position: 'relative' }} id="cesiumContainer">
        <Box zIndex={1200} margin={'10px'} position={'absolute'} left={0} top={0} display="flex" flexDirection="column" gap={1}>
          <Collapse orientation={'horizontal'} in={showModelTree && modelNames.length > 1} unmountOnExit>
            <CesiumModelTree
              models={models}
              onToggleVisible={handleToggleModel}
              onZoomTo={handleZoomToModel}
              onClose={() => {
                modelTreeAutoClosed.current = false;
                setShowModelTree(false);
              }}
            />
          </Collapse>
          <Collapse orientation={'horizontal'} in={isOpenGFI}>
            {selectedEntityId !== 0 && (
              <EntityInfoPane
                focusOnObject={focusOnObject}
                onClose={handleCloseInfoPane}
                trackedEntityId={trackedEntityId}
                entityId={selectedEntityId}
                record={selectedEntityRecord}
                selectedEntityType={selectedEntityType} 
              />
            )}
          </Collapse>
        </Box>
        {collectedPoints.length ? (
          <Box zIndex={1200} margin={'10px'} position={'absolute'} right={0} top={50}>
            <Collapse orientation={'horizontal'} in={!!collectedPoints.length}>
              <ClickedPointTable points={collectedPoints} removePoint={removePoint} />
            </Collapse>
          </Box>
        ) : null}
      </div>
    </>
  );
};

export default CesiumPage;
